import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShieldIcon, FileTextIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [activeModal]);
  return <>
      <footer className="border-t border-gray-800 py-8 relative z-10" data-id="element-52">
        <div className="max-w-4xl mx-auto px-4 flex flex-col items-center gap-4" data-id="element-53">
          <div className="flex items-center gap-6 font-mono text-xs" data-id="element-54">
            <button onClick={() => setActiveModal('privacy')} className="text-gray-500 hover:text-cyber-neon transition-colors duration-200 flex items-center gap-1.5" data-id="element-55">
              <ShieldIcon className="w-3 h-3" data-id="element-56" />
              Política de Privacidad
            </button>
            <span className="text-gray-700" data-id="element-57">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-gray-500 hover:text-cyber-neon transition-colors duration-200 flex items-center gap-1.5" data-id="element-58">
              <FileTextIcon className="w-3 h-3" data-id="element-59" />
              Términos y Condiciones
            </button>
          </div>
          <p className="text-gray-600 font-mono text-xs" data-id="element-60">
            © 2026 Social Hacker v1.0. Todos los derechos reservados.
          </p>
          <p className="text-gray-700 font-mono text-[10px]" data-id="element-61">
            SYSTEM_TERMINATED
          </p>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-62">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-63">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-64" />

            {/* Modal Content */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative w-full max-w-2xl max-h-[80vh] bg-cyber-dark border border-cyber-neon/40 rounded-xl shadow-neon overflow-hidden" data-id="element-65">
              {/* Header */}
              <div className="sticky top-0 bg-cyber-dark border-b border-cyber-neon/20 px-6 py-4 flex items-center justify-between z-10" data-id="element-66">
                <h3 className="text-cyber-neon font-mono font-bold text-lg flex items-center gap-2" data-id="element-67">
                  {activeModal === 'privacy' ? <>
                      <ShieldIcon className="w-5 h-5" data-id="element-68" />
                      Política de Privacidad
                    </> : <>
                      <FileTextIcon className="w-5 h-5" data-id="element-69" />
                      Términos y Condiciones
                    </>}
                </h3>
                <button onClick={() => setActiveModal(null)} className="text-gray-500 hover:text-cyber-neon transition-colors p-1 rounded hover:bg-cyber-neon/10" data-id="element-70">
                  <XIcon className="w-5 h-5" data-id="element-71" />
                </button>
              </div>

              {/* Scrollable Body */}
              <div className="overflow-y-auto max-h-[calc(80vh-64px)] p-6 text-gray-300 text-sm leading-relaxed space-y-6 font-sans" data-id="element-72">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-73" /> : <TermsContent data-id="element-74" />}
              </div>

              {/* Bottom glow line */}
              <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyber-neon/30 to-transparent" data-id="element-75" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-gray-500 font-mono text-xs" data-id="element-76">
        Última actualización: Marzo 2026
      </p>

      <div data-id="element-77">
        <h4 className="text-white font-mono font-bold mb-2 text-base" data-id="element-78">
          1. Información que Recopilamos
        </h4>
        <p data-id="element-79">
          Al adquirir Social Hacker v1.0, recopilamos únicamente la información
          necesaria para procesar tu compra y entregarte el producto digital: tu
          dirección de correo electrónico y datos de pago procesados de forma
          segura a través de nuestra pasarela de pagos.
        </p>
      </div>

      <div data-id="element-80">
        <h4 className="text-white font-mono font-bold mb-2 text-base" data-id="element-81">
          2. Uso de la Información
        </h4>
        <p data-id="element-82">Tu información personal se utiliza exclusivamente para:</p>
        <ul className="list-none space-y-2 mt-2 ml-2" data-id="element-83">
          <li className="flex items-start gap-2" data-id="element-84">
            <span className="text-cyber-neon font-mono" data-id="element-85">{'>'}</span>
            Entregar el material digital adquirido a tu correo electrónico.
          </li>
          <li className="flex items-start gap-2" data-id="element-86">
            <span className="text-cyber-neon font-mono" data-id="element-87">{'>'}</span>
            Procesar solicitudes de reembolso dentro del período de garantía.
          </li>
          <li className="flex items-start gap-2" data-id="element-88">
            <span className="text-cyber-neon font-mono" data-id="element-89">{'>'}</span>
            Enviarte actualizaciones importantes sobre el producto (puedes darte
            de baja en cualquier momento).
          </li>
        </ul>
      </div>

      <div data-id="element-90">
        <h4 className="text-white font-mono font-bold mb-2 text-base" data-id="element-91">
          3. Protección de Datos
        </h4>
        <p data-id="element-92">
          Implementamos medidas de seguridad estándar de la industria para
          proteger tu información personal. No almacenamos datos de tarjetas de
          crédito ni información financiera sensible en nuestros servidores.
        </p>
      </div>

      <div data-id="element-93">
        <h4 className="text-white font-mono font-bold mb-2 text-base" data-id="element-94">
          4. Compartir Información
        </h4>
        <p data-id="element-95">
          No vendemos, intercambiamos ni transferimos tu información personal a
          terceros. La única excepción son los proveedores de servicios de
          confianza que nos ayudan a operar nuestro sitio web y procesar pagos,
          siempre bajo estrictos acuerdos de confidencialidad.
        </p>
      </div>

      <div data-id="element-96">
        <h4 className="text-white font-mono font-bold mb-2 text-base" data-id="element-97">
          5. Cookies
        </h4>
        <p data-id="element-98">
          Utilizamos cookies esenciales para el funcionamiento del sitio y
          cookies analíticas para mejorar tu experiencia. Puedes desactivar las
          cookies en la configuración de tu navegador.
        </p>
      </div>

      <div data-id="element-99">
        <h4 className="text-white font-mono font-bold mb-2 text-base" data-id="element-100">
          6. Tus Derechos
        </h4>
        <p data-id="element-101">
          Tienes derecho a acceder, rectificar o eliminar tus datos personales
          en cualquier momento. Para ejercer estos derechos, puedes comunicarte
          con nosotros y procesaremos tu solicitud en un plazo máximo de 30
          días.
        </p>
      </div>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-gray-500 font-mono text-xs" data-id="element-102">
        Última actualización: Marzo 2026
      </p>

      <div data-id="element-103">
        <h4 className="text-white font-mono font-bold mb-2 text-base" data-id="element-104">
          1. Aceptación de los Términos
        </h4>
        <p data-id="element-105">
          Al acceder y adquirir Social Hacker v1.0, aceptas estos términos y
          condiciones en su totalidad. Si no estás de acuerdo con alguno de
          estos términos, te pedimos que no realices la compra.
        </p>
      </div>

      <div data-id="element-106">
        <h4 className="text-white font-mono font-bold mb-2 text-base" data-id="element-107">
          2. Producto Digital
        </h4>
        <p data-id="element-108">
          Social Hacker v1.0 es un producto digital (ebook) de 60 páginas
          entregado en formato PDF. El acceso se proporciona de manera
          instantánea a través del correo electrónico proporcionado al momento
          de la compra.
        </p>
      </div>

      <div data-id="element-109">
        <h4 className="text-white font-mono font-bold mb-2 text-base" data-id="element-110">
          3. Licencia de Uso
        </h4>
        <p data-id="element-111">
          Al adquirir el producto, recibes una licencia personal e
          intransferible para uso individual. Queda estrictamente prohibido:
        </p>
        <ul className="list-none space-y-2 mt-2 ml-2" data-id="element-112">
          <li className="flex items-start gap-2" data-id="element-113">
            <span className="text-cyber-red font-mono" data-id="element-114">✕</span>
            Reproducir, distribuir o compartir el contenido con terceros.
          </li>
          <li className="flex items-start gap-2" data-id="element-115">
            <span className="text-cyber-red font-mono" data-id="element-116">✕</span>
            Revender o sublicenciar el producto.
          </li>
          <li className="flex items-start gap-2" data-id="element-117">
            <span className="text-cyber-red font-mono" data-id="element-118">✕</span>
            Modificar el contenido para crear obras derivadas con fines
            comerciales.
          </li>
        </ul>
      </div>

      <div data-id="element-119">
        <h4 className="text-white font-mono font-bold mb-2 text-base" data-id="element-120">
          4. Garantía de Reembolso
        </h4>
        <p data-id="element-121">
          Ofrecemos una garantía de devolución de dinero de 7 días. Si después
          de aplicar el protocolo durante una semana no estás satisfecho con los
          resultados, puedes solicitar un reembolso completo del 100% sin
          preguntas. El reembolso se procesará en un plazo de 5 a 10 días
          hábiles.
        </p>
      </div>

      <div data-id="element-122">
        <h4 className="text-white font-mono font-bold mb-2 text-base" data-id="element-123">
          5. Descargo de Responsabilidad
        </h4>
        <p data-id="element-124">
          Social Hacker v1.0 es un recurso educativo e informativo. No sustituye
          el tratamiento profesional de salud mental. Si experimentas ansiedad
          severa o cualquier condición de salud mental, te recomendamos
          consultar con un profesional de la salud. Los resultados pueden variar
          según cada individuo.
        </p>
      </div>

      <div data-id="element-125">
        <h4 className="text-white font-mono font-bold mb-2 text-base" data-id="element-126">
          6. Precio y Disponibilidad
        </h4>
        <p data-id="element-127">
          El precio promocional de lanzamiento de $6.36 USD está sujeto a
          disponibilidad y puede cambiar sin previo aviso. El precio regular del
          producto es de $42.50 USD. Nos reservamos el derecho de modificar
          precios y ofertas en cualquier momento.
        </p>
      </div>

      <div data-id="element-128">
        <h4 className="text-white font-mono font-bold mb-2 text-base" data-id="element-129">
          7. Modificaciones
        </h4>
        <p data-id="element-130">
          Nos reservamos el derecho de actualizar estos términos y condiciones
          en cualquier momento. Las modificaciones entrarán en vigor
          inmediatamente después de su publicación en esta página.
        </p>
      </div>
    </>;
}